"use client";

import { Button } from "@ui/components/core/Button";
import { clsx } from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useId, useState } from "react";
import type { QuoteCarouselBlock } from "@mw/shared/payload-types";
import { useTranslations } from "next-intl";

export function BlockQuoteCarouselMinimal({ quotes, label }: QuoteCarouselBlock) {
  const t = useTranslations("ClientKeys.Globals");
  const tQuoteCarousel = useTranslations("ClientKeys.BlockQuoteCarousel");
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const id = useId();

  if (!quotes || quotes.length === 0) {
    return null;
  }

  const goPrev = () => {
    setDirection("backward");
    setActiveIndex(a => (a - 1 + quotes.length) % quotes.length);
  };

  const goNext = () => {
    setDirection("forward");
    setActiveIndex(a => (a + 1) % quotes.length);
  };

  const goTo = (index: number) => {
    setDirection(index < activeIndex ? "backward" : "forward");
    setActiveIndex(index);
  };

  return (
    <div className='tw-flex tw-flex-col tw-items-center tw-gap-10' aria-roledescription='carousel' aria-label={label}>
      <div
        id={`${id}-slides`}
        className='tw-relative tw-grid tw-w-full tw-max-w-2xl tw-grid-cols-1 tw-grid-rows-1 tw-px-4'
      >
        {quotes.map((quote, quoteIndex) => (
          <div
            key={quoteIndex}
            className={clsx(
              "tw-col-start-1 tw-row-start-1 tw-text-center tw-transition-opacity tw-duration-300",
              quoteIndex === activeIndex ? "tw-opacity-100 tw-delay-150" : "tw-opacity-0",
            )}
            style={{ zIndex: quoteIndex === activeIndex ? 1 : 0 }}
            inert={quoteIndex !== activeIndex}
            role='group'
            aria-roledescription='slide'
            aria-label={tQuoteCarousel("slide_label", { number: quoteIndex + 1, total: quotes.length })}
          >
            <figure>
              <blockquote>
                <p className='tw-type-2xl lg:tw-type-3xl tw-text-black'>
                  <span className='tw-text-balance before:tw-content-[open-quote] after:tw-content-[close-quote]'>
                    {quote.quote}
                  </span>
                </p>
              </blockquote>
              <figcaption className='tw-mt-7 tw-type-xs tw-text-gray-500'>
                {quote.author?.name}
                {quote.author?.role ? `, ${quote.author.role}` : ""}
              </figcaption>
            </figure>
          </div>
        ))}
      </div>
      {quotes.length > 1 && (
        <div className='tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-6 tw-px-4'>
          <Button
            aria-label={t("previous_slide")}
            aria-controls={`${id}-slides`}
            icon={<ChevronLeft size={16} />}
            size='pill-md'
            variant='elevated'
            className='tw-shrink-0'
            onClick={goPrev}
          />
          <div className='tw-flex tw-min-w-0 tw-max-w-64 tw-items-center tw-gap-2'>
            {quotes.map((_, dotIndex) => (
              <button
                key={dotIndex}
                type='button'
                aria-label={tQuoteCarousel("go_to_slide", { number: dotIndex + 1 })}
                aria-controls={`${id}-slides`}
                aria-current={dotIndex === activeIndex}
                className='tw-w-6 tw-min-w-0 tw-py-2'
                onClick={() => goTo(dotIndex)}
              >
                <span className='tw-relative tw-block tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-gray-200'>
                  <span
                    className={clsx(
                      "tw-absolute tw-inset-0 tw-rounded-full tw-bg-black tw-transition-[transform,opacity] tw-duration-500 tw-ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:tw-transition-none",
                      dotIndex === activeIndex
                        ? [
                            "tw-scale-x-100 tw-opacity-100",
                            direction === "forward" ? "tw-origin-left" : "tw-origin-right",
                          ]
                        : ["tw-scale-x-0 tw-opacity-0", direction === "forward" ? "tw-origin-right" : "tw-origin-left"],
                    )}
                  />
                </span>
              </button>
            ))}
          </div>
          <Button
            aria-label={t("next_slide")}
            aria-controls={`${id}-slides`}
            icon={<ChevronRight size={16} />}
            size='pill-md'
            variant='elevated'
            className='tw-shrink-0'
            onClick={goNext}
          />
        </div>
      )}
    </div>
  );
}
